import React, { useEffect, useRef, useState } from 'react';
import { styles } from './MenuDropdown.css';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { MenuItem, MenuItemProps } from '../MenuItem/MenuItem';
import { DropdownHeading } from './DropdownHeading/DropdownHeading';
import { useTrackingContext } from '../../Tracking';

const MAX_OVERFLOW_SHADOW_HEIGHT = 25;

export type MenuDropdownProps = {
  label: string;
  menuItems: Array<MenuItemProps>;
  isOpen: boolean;
  onBack?: (e: React.MouseEvent) => void;
  className?: string;
  isAnnouncementBarActive?: boolean;
  trackingData?: { nesting?: Array<string | undefined> };
};

export const MenuDropdown = ({
  menuItems,
  label,
  className,
  isOpen,
  onBack,
  isAnnouncementBarActive,
  trackingData,
}: MenuDropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [overflowShadowHeight, setOverflowShadowHeight] = useState(MAX_OVERFLOW_SHADOW_HEIGHT);
  const { tracker } = useTrackingContext();

  useEffect(() => {
    if (ref.current) {
      const { current } = ref;
      setIsOverflowing(current.scrollHeight > current.clientHeight);
    }
  }, [ref, isOpen]);

  const handleScroll = () => {
    if (ref.current) {
      const maxScroll = ref.current.scrollHeight - ref.current.clientHeight;
      const scrollGap = maxScroll - ref.current.scrollTop;
      if (scrollGap > MAX_OVERFLOW_SHADOW_HEIGHT) {
        return setOverflowShadowHeight(MAX_OVERFLOW_SHADOW_HEIGHT);
      }
      setOverflowShadowHeight(scrollGap);
    }
  };

  useEffect(() => {
    const current = ref.current;
    if (current) {
      current.addEventListener('scroll', handleScroll);
    }
    return () => {
      current?.removeEventListener('scroll', handleScroll);
    };
  }, [ref]);

  const handleClick = (event: React.MouseEvent, menuItem: MenuItemProps) => {
    tracker.trackCustomEvent({
      event: `custom.menu.click`,
      group: tracker.group,
      label: menuItem.label,
      type: menuItem?.href ? 'open' : 'expand',
      info: {
        nesting: `${trackingData?.nesting ? `${trackingData?.nesting?.filter(Boolean).join(' | ')} | ` : ''}${
          menuItem.label
        }`,
        ...(menuItem?.href ? { url: menuItem?.href } : {}),
      },
    });
    menuItem.onClick?.(event);
  };

  return (
    <div
      className={classNames([
        styles.container({
          visibility: isOpen ? 'visible' : 'hidden',
        }),
        className,
      ])}
      data-testid="navigation-menu-dropdown"
    >
      <DropdownHeading label={label} onBack={onBack} />
      <div ref={ref} className={styles.menu({ isAnnouncementBarActive })} data-testid="navigation-menu-dropdown-menu">
        <ul className={styles.list} data-testid="navigation-menu-dropdown-list">
          {menuItems.map((menuItem, i) => (
            <MenuItem
              key={i}
              type={menuItem.type}
              label={menuItem.label}
              onClick={e => handleClick(e, menuItem)}
              href={menuItem.href}
              isActive={menuItem.isActive}
              shouldHideArrow={menuItem.shouldHideArrow}
              count={menuItem.count}
              leftIcon={menuItem.leftIcon}
              isBold={menuItem.isBold}
              subMenuItems={menuItem.subMenuItems}
              onBack={menuItem.onBack}
              isAnnouncementBarActive={isAnnouncementBarActive}
              trackingData={trackingData}
            />
          ))}
        </ul>
        <div
          className={styles.overflowShadow({ isVisible: isOverflowing })}
          style={{ height: overflowShadowHeight }}
        ></div>
      </div>
    </div>
  );
};
